<template>
    <component :is="$auth.isSubdomainActive() ? 'div' : 'common-page-plain'">
        <common-content-wrapper width="large">
            <div class="single-reservation">
                <common-content-spacer height="medium" :isCenter="true">
                    <div class="single-reservation__cover">
                        <img class="single-reservation__cover-image" :src="coverImage" :alt="$t('alt.offer_cover')" />
                    </div>
                    <div class="single-reservation__header">
                        <common-page-header
                            v-if="reservation.availabilityOffer"
                            :isCenter="true"
                            :heading="getTranslation(reservation?.availabilityOffer?.linkedOffer?.titleTextContent)"
                        ></common-page-header>
                    </div>
                    <div v-if="isOfferOwner" class="single-reservation__actions">
                        <div
                            v-if="reservation.availabilityOffer?.linkedOffer?.location.companyServiceProvider"
                            class="single-reservation__actions--item single-reservation__actions--item--history"
                            @click="openClientHistoryModal(item)"
                        >
                            <p>
                                {{ $t('pages.company.company_clients.history') }}
                            </p>
                            <common-icon class="clients-list--history" name="reservation" size="large"></common-icon>
                        </div>
                        <div
                            v-if="reservation.availabilityOffer?.linkedOffer?.location.companyServiceProvider"
                            class="single-reservation__actions--item single-reservation__actions--item--records"
                            @click="openClientRecordModal(item)"
                        >
                            <p>
                                {{ $t('pages.company.company_clients.records') }}
                            </p>
                            <common-icon class="clients-list--record" name="records" size="large"></common-icon>
                        </div>
                        <div
                            class="single-reservation__actions--item single-reservation__actions--item--note"
                            @click="openReservationNoteModal(item)"
                        >
                            <p>
                                {{ $t('common.microcopy.note') }}
                            </p>
                            <common-icon class="clients-list--record" name="notes" size="large"></common-icon>
                        </div>
                    </div>
                    <div v-if="isOfferOwner && reservation?.moderatorNote?.length">
                        <span class="single-reservation__note">{{ $t('common.microcopy.note') }}:</span>
                        {{ getModeratorNote }}
                    </div>
                    <div class="single-reservation__categories">
                        <p v-for="(item, index) in getCategoriesTitle" :key="index">{{ item }}</p>
                    </div>
                    <reservation-status v-if="reservation.status" :status="reservation.status" size="large"></reservation-status>
                    <div class="single-reservation__info">
                        <div class="single-reservation__info--user">
                            <common-user-avatar
                                :user="reservation.initiator"
                                :shouldLeadToProfile="true"
                                size="medium-large"
                                class="single-reservation__info--user--icon-first"
                            ></common-user-avatar>
                            <p class="single-reservation__info--user--paragraph-first">
                                {{ getInitiator }}
                            </p>
                        </div>
                        <common-icon
                            v-if="reservation.availabilityOffer?.linkedOffer?.location.companyServiceProvider"
                            :name="handIcon"
                            size="huge"
                            class="single-reservation__info--icon"
                        ></common-icon>
                        <div
                            v-if="reservation.availabilityOffer?.linkedOffer?.location.companyServiceProvider"
                            class="single-reservation__info--user"
                        >
                            <common-company-avatar
                                v-if="reservation.availabilityOffer?.linkedOffer?.location.companyServiceProvider"
                                :company="reservation.availabilityOffer?.linkedOffer?.location.companyServiceProvider"
                                :shouldLeadToProfile="true"
                                size="tiny-small"
                                class="single-reservation__info--user--icon-second"
                            ></common-company-avatar>
                            <p class="single-reservation__info--user--paragraph-second">
                                {{ companyName }}
                            </p>
                        </div>
                        <common-icon :name="handIcon" size="extra-huge" class="single-reservation__info--icon"></common-icon>
                        <div class="single-reservation__info--user">
                            <common-user-avatar
                                :user="reservation.availabilityOffer?.availability?.availabilityUsers[0]?.user"
                                :shouldLeadToProfile="true"
                                size="ultra-large"
                            ></common-user-avatar>
                            <p>
                                {{ userFullName }}
                            </p>
                        </div>
                    </div>
                    <common-content-wrapper width="small">
                        <common-content-spacer height="tiny" :isCenter="true">
                            <div class="single-reservation__location">
                                <common-icon name="location" size="large"></common-icon>
                                <a
                                    :href="getGoogleMapLocation(reservation.availabilityOffer?.linkedOffer.location)"
                                    target="_blank"
                                >
                                    {{ offerAddress }}
                                </a>
                            </div>
                            <div
                                v-if="reservation?.initiator?.id !== $auth.getUser().id && isOfferOwner"
                                class="single-reservation__phone"
                            >
                                <div class="single-reservation__phone--name">
                                    <common-icon name="phone" size="medium"></common-icon>
                                    {{ getInitiator.trim(' ') }}:
                                </div>
                                <a
                                    v-if="isPhoneShow"
                                    class="single-reservation__phone--text"
                                    :href="`tel:${reservation?.initiator?.mobile}`"
                                >
                                    {{ reservation?.initiator?.mobile }}
                                </a>
                                <a v-else class="single-reservation__phone--text" @click="isPhoneShow = true">
                                    {{ $t('components.user.user_card.click_phone') }}
                                </a>
                            </div>
                            <div>
                                <common-button
                                    v-if="showSendSms"
                                    type="secondary"
                                    size="micro"
                                    :label="$t('common.microcopy.send_sms')"
                                    @click="sendMessage()"
                                ></common-button>
                            </div>
                            <div class="single-reservation__time">
                                <common-icon name="clock" size="large"></common-icon>
                                <p>{{ offerTime }}</p>
                            </div>
                        </common-content-spacer>
                        <div v-if="reservation.allowOverlapAfterMinutes" class="single-reservation__time">
                            <common-icon name="timedelay" size="medium"></common-icon>
                            <p>{{ getOfferEndTimeWithOverlap }}</p>
                        </div>
                        <div :class="priceClass">
                            <p :class="{ 'single-reservation__price--paid': offerPrice !== paidOfferPrice }">{{ offerPrice }}</p>
                            <p v-if="offerPrice !== paidOfferPrice">{{ paidOfferPrice }}</p>
                            <common-icon
                                v-if="isOfferOwner"
                                name="edit"
                                size="extra-small"
                                @click="openEditPriceModal()"
                            ></common-icon>
                        </div>
                        <div class="single-reservation__description">
                            <p>{{ reservation.description }}</p>
                        </div>
                        <div v-if="!$auth.isUserAdmin()" class="add-to-calendar__button">
                            <div class="add-to-calendar">
                                <add-to-calendar-button
                                    v-if="showAddToCalendarButton"
                                    :name="getTranslation(reservation?.availabilityOffer?.linkedOffer?.titleTextContent)"
                                    :description="getDescriptionForCalendar"
                                    :startDate="reservation.date"
                                    :endDate="getReservationEndDate"
                                    :startTime="getOfferStartTimeUtc"
                                    :endTime="getOfferEndTimeUtc"
                                    :timeZone="reservation.availabilityOffer?.linkedOffer?.location.timeZone"
                                    :label="$t('components.reservations.reservation_list.other.add_to_calendar')"
                                    :options="['Apple', 'Google']"
                                    :location="getReservationLocation"
                                    trigger="click"
                                    @click="calendarButtonClick()"
                                ></add-to-calendar-button>
                                <span v-if="showAddToCalendarButton" :class="tooltipClass">
                                    <common-info-tip>
                                        <span>
                                            {{ $t('components.reservations.reservation_list.other.tooltip') }}
                                        </span>
                                    </common-info-tip>
                                </span>
                            </div>
                        </div>
                    </common-content-wrapper>
                    <common-buttons v-if="!$auth.isUserAdmin()">
                        <common-button
                            v-if="availableActions.includes(reservationsAction.CANCEL)"
                            :isProcessing="isLoading"
                            type="secondary"
                            :label="$t('components.reservations.reservation_status.actions.CANCEL')"
                            @click="toogleSatusCancelModal"
                        ></common-button>
                        <common-button
                            v-if="availableActions.includes(reservationsAction.RESCHEDULE)"
                            :isProcessing="isLoading"
                            type="tertiary"
                            :label="$t('components.reservations.reservation_status.actions.RESCHEDULE')"
                            @click="rescheduleReservation"
                        ></common-button>
                        <common-button
                            v-if="availableActions.includes(reservationsAction.DECLINE)"
                            :isProcessing="isLoading"
                            type="secondary"
                            :label="$t('components.reservations.reservation_status.actions.DECLINE')"
                            @click="toggleDeclineReservationModal"
                        ></common-button>
                        <common-button
                            v-if="availableActions.includes(reservationsAction.ACCEPT)"
                            :isProcessing="isLoading"
                            type="primary"
                            :label="$t('components.reservations.reservation_status.actions.ACCEPT')"
                            @click="changeReservationStatus(reservationStatus.ACCEPTED)"
                        ></common-button>
                        <common-button
                            v-if="isFinishReservationAvailable"
                            :isProcessing="isLoading"
                            type="primary"
                            :label="$t('components.reservations.reservation_status.actions.FINISH')"
                            @click="toggleFinishReservationModal"
                        ></common-button>
                        <common-button
                            v-if="isFinishEearlyReservationAvailable"
                            :isProcessing="isLoading"
                            type="primary"
                            :label="$t('components.reservations.reservation_status.actions.FINISH_EARLY')"
                            @click="toggleFinishEarlyReservationModal()"
                        ></common-button>
                        <common-button
                            v-if="isCancelFinishEarlyReservationAvailable"
                            :isProcessing="isLoading"
                            type="primary"
                            :label="$t('components.reservations.reservation_status.actions.REMOVE_FINISH_EARLY')"
                            @click="toggleCancelFinishEarlyReservationModal()"
                        ></common-button>
                    </common-buttons>
                    <common-yes-no-modal
                        v-if="isStatusCancelModalOpen"
                        :showWarningIcon="true"
                        height="tiny"
                        :headingText="$t('components.reservations.reservation_status.modal.cancel_heading')"
                        :noButtonText="$t('common.microcopy.false')"
                        :yesButtonText="$t('common.microcopy.true')"
                        @close="toogleSatusCancelModal"
                        @continue="closeSatusCancelModal()"
                    ></common-yes-no-modal>
                    <decline-reservation-modal
                        v-if="isDeclineReservationModalOpen"
                        :isLoading="isLoading"
                        :decline="declineReservationModalProperties.label"
                        :cancel="$t('common.microcopy.back')"
                        :headingText="declineReservationModalProperties.heading"
                        @decline="changeReservationStatus(declineReservationModalProperties.action)"
                        @setUnavailable="changeReservationStatus(declineReservationModalProperties.action, true)"
                        @close="toggleDeclineReservationModal"
                    ></decline-reservation-modal>
                    <finish-reservation-modal
                        v-if="isFinishReservationModalOpen"
                        :noShow="$t('components.reservations.reservation_status.actions.NO_SHOW')"
                        :done="$t('components.reservations.reservation_status.actions.DONE')"
                        :headingText="$t('components.reservations.reservation_status.modal.no_show_headling')"
                        @done="changeReservationStatus(reservationStatus.DONE)"
                        @noShow="changeReservationStatus(reservationStatus.NO_SHOW)"
                        @close="toggleFinishReservationModal"
                    ></finish-reservation-modal>
                    <finish-early-reservation-modal
                        v-if="isFinishEarlyReservationModalOpen"
                        :done="$t('components.reservations.reservation_status.actions.FINISH_EARLY')"
                        :headingText="$t('components.reservations.reservation_status.modal.no_show_headling')"
                        :maxMinutesAvailable="maxAviaibleMinutesForFinishEaryl"
                        @minutes-chosen="finishReservationEarly"
                        @close="toggleFinishEarlyReservationModal"
                    ></finish-early-reservation-modal>
                    <reservation-note-modal
                        v-if="isReservationNoteModalOpen"
                        :companyId="reservation?.availabilityOffer?.linkedOffer?.location?.companyServiceProvider?.id"
                        :reservationId="reservation.id"
                        :moderatorNote="reservation.moderatorNote"
                        @close="closeReservationNoteModal"
                        @moderator-note-saved="onModeratorNoteSaved"
                    ></reservation-note-modal>
                    <common-yes-no-modal
                        v-if="isCancelFinishEarlyReservationModalOpen"
                        height="tiny"
                        :headingText="$t('components.reservations.reservation_status.modal.remove_finish_early_modal')"
                        :noButtonText="$t('common.microcopy.false')"
                        :yesButtonText="$t('common.microcopy.true')"
                        @close="toggleCancelFinishEarlyReservationModal"
                        @continue="cancelFinishEarlyReservation()"
                    ></common-yes-no-modal>
                    <client-record-modal
                        v-if="isClientRecordModalOpen"
                        :clientId="reservation?.initiator.id"
                        :companyId="reservation?.availabilityOffer?.linkedOffer?.location?.companyServiceProvider?.id"
                        :clientRecordsProp="reservation.initiator"
                        @close="closeClientRecordModal"
                    ></client-record-modal>
                    <client-history-modal
                        v-if="isClientHistoryModalOpen"
                        :clientHistoryProp="reservation?.initiator"
                        @close="closeClientHistoryModal"
                    ></client-history-modal>
                    <edit-price-modal
                        v-if="isEditPriceModalOpen"
                        :offerPrice="paidOfferPrice"
                        :currency="reservation.currency"
                        @close="closeEditPriceModal"
                        @price-updated="onPriceUpdated"
                    ></edit-price-modal>
                </common-content-spacer>
            </div>
        </common-content-wrapper>
    </component>
</template>

<script>
    import ReservationStatus from 'components/page-elements/reservations/ReservationStatus'
    import CalendarEventAPI from 'api/CalendarEventAPI'
    import ReservationService from 'services/ReservationsService'
    import ReservationActionEnum from 'enums/reservation_action.enum'
    import ReservationStatusEnum from 'enums/reservation_status.enum'
    import RoutesEnum from 'enums/routes.enum'
    import availabilityTransformer from 'transformers/availabilityTransformer'
    import dayjs from 'dayjs'
    import dayjsPluginUTC from 'dayjs/plugin/utc'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import DeclineReservationModal from 'components/modals/offer/DeclineReservationModal.vue'
    import PriceMixin from 'mixins/PriceMixin'
    import AddToCalendarButton from 'add-to-calendar-button-vue'
    import UserAvailabilityEnum from 'enums/user_availability_role.enum'
    import FinishReservationModal from 'components/modals/offer/FinishReservationModal.vue'
    import CalendarEventEnums from 'enums/calendar_event.enum'
    import TranslateMixin from 'mixins/TranslateMixin'
    import LanguageEnum from 'enums/language.enum'
    import FinishEarlyReservationModal from 'components/modals/offer/FinishEarlyReservationModal.vue'
    import ReservationNoteModal from 'components/modals/offer/ReservationNoteModal.vue'
    import ClientRecordModal from 'components/modals/company/ClientRecordModal'
    import ClientHistoryModal from 'components/modals/company/ClientHistoryModal'
    import EditPriceModal from 'components/modals/calendar/EditPriceModal'
    import OfferReservationTypeEnum from 'enums/offer_reservation_type.enum'

    dayjs.extend(dayjsPluginUTC)

    export default {
        mixins: [WindowResizeMixin, PriceMixin, TranslateMixin],

        components: {
            ReservationStatus,
            DeclineReservationModal,
            AddToCalendarButton,
            FinishReservationModal,
            FinishEarlyReservationModal,
            ClientRecordModal,
            ClientHistoryModal,
            ReservationNoteModal,
            EditPriceModal,
        },

        data: () => ({
            reservation: {},
            isLoading: false,
            isStatusCancelModalOpen: false,
            isDeclineReservationModalOpen: false,
            isFinishReservationModalOpen: false,
            declineReservationModalProperties: {},
            isEmailCopied: false,
            isFinishEarlyReservationModalOpen: false,
            isCancelFinishEarlyReservationModalOpen: false,
            isPhoneShow: false,
            isClientRecordModalOpen: false,
            isClientHistoryModalOpen: false,
            isReservationNoteModalOpen: false,
            isEditPriceModalOpen: false,
        }),

        computed: {
            maxAviaibleMinutesForFinishEaryl() {
                return this.reservation?.endTime - this.reservation?.startTime - 5
            },
            tooltipClass() {
                return {
                    'add-to-calendar__info': true,
                    'add-to-calendar__info--ru': this.$i18n.locale === LanguageEnum.RU,
                }
            },

            priceClass() {
                return {
                    'single-reservation__price': true,
                    'single-reservation__price--hidden': this.isPriceHidden,
                }
            },

            isPriceHidden() {
                return (
                    this.reservation?.initiator?.id === this.$auth.getUser().id &&
                    !this.isOfferOwner &&
                    this.reservation.availabilityOffer.linkedOffer.hidePrice
                )
            },

            getModeratorNote() {
                return this.reservation?.moderatorNote?.length > 50
                    ? `${this.reservation.moderatorNote.substring(0, 50)}...`
                    : this.reservation?.moderatorNote
            },

            offerPrice() {
                if (this.isPriceHidden) {
                    return this.reservation.currency
                }
                let price = this.setPrice(this.reservation[`price${this.reservation.currency}`])

                return `${price} ${this.reservation.currency}`
            },
            paidOfferPrice() {
                if (this.isPriceHidden) {
                    return this.reservation.currency
                }
                let price = this.setPrice(this.reservation[`paidPrice${this.reservation.currency}`])

                return `${price} ${this.reservation.currency}`
            },
            availableActions() {
                return ReservationService.getReservationActionsByStatus(
                    this.reservation.status,
                    this.isOfferOwner,
                    this.isReservationInPast,
                    this.isEventInMoreThanOneMinute,
                    this.isEventInMoreThan20Minutes,
                    this.isEventEligibleToReschedule,
                    this.isEventEligibleToCancel,
                    this.isPublicReservationBlocked,
                )
            },
            reservationsAction() {
                return ReservationActionEnum
            },
            reservationStatus() {
                return ReservationStatusEnum
            },
            isOfferOwner() {
                const userId = this.$auth.getUser().id
                return !!this.reservation.availabilityOffer?.availability?.availabilityUsers?.find((u) => u.user.id === userId)
            },
            coverImage() {
                return this.reservation.availabilityOffer?.linkedOffer?.location?.companyServiceProvider?.photoCover
                    ? this.reservation.availabilityOffer?.linkedOffer?.location?.companyServiceProvider?.photoCover
                    : require('../../assets/images/company-cover-placeholder.png')
            },
            companyName() {
                return this.reservation.availabilityOffer?.linkedOffer.location.companyServiceProvider.name
            },
            userFullName() {
                const user = this.getPerformer
                if (user?.id === this.$auth.getUser().id) {
                    return this.$t('common.microcopy.you')
                } else {
                    return `${user?.firstName} ${user?.lastName}`
                }
            },

            getCategoriesTitle() {
                return this.reservation.calendarEventItems?.map((item) => {
                    return this.getTranslation(item.offerCategory.titleTextContent)
                })
            },

            getInitiator() {
                if (this.reservation?.initiator?.id === this.$auth.getUser().id) {
                    return this.$t('common.microcopy.you')
                } else {
                    return `${this.reservation?.initiator?.firstName} ${this.reservation?.initiator?.lastName}`
                }
            },

            offerAddress() {
                return `${this.reservation.availabilityOffer?.linkedOffer?.location.address} ${this.reservation.availabilityOffer?.linkedOffer?.location.city.name}`
            },
            offerTime() {
                return `${this.getOfferDate} ${this.getOfferStartTime} - ${this.getOfferEndTime}`
            },
            getOfferDate() {
                return this.getDate(this.reservation?.date)
            },
            getOfferStartTime() {
                return this.getTime(
                    this.reservation?.startTime,
                    this.reservation.availabilityOffer?.linkedOffer?.location.timeZone,
                )
            },

            getOfferStartTimeUtc() {
                return availabilityTransformer.minutesDateToLocalTZTime(this.reservation?.startTime)
            },
            getOfferEndTimeUtc() {
                return availabilityTransformer.minutesDateToLocalTZTime(this.reservation?.endTime)
            },

            getReservationEndDate() {
                const reservationDate = new Date(this.reservation.date)

                if (this.getOfferEndTime === '00:00') {
                    reservationDate.setDate(reservationDate.getDate() + 1)
                }

                return reservationDate.toISOString().split('T')[0]
            },

            getOfferEndTime() {
                return this.getTime(this.reservation?.endTime, this.reservation.availabilityOffer?.linkedOffer?.location.timeZone)
            },

            getOfferEndTimeWithOverlap() {
                if (this.reservation.allowOverlapAfterMinutes) {
                    return `${this.getOfferDate} ${this.getOfferStartTime} - ${this.getTime(
                        this.reservation?.startTime + this.reservation.allowOverlapAfterMinutes,
                        this.reservation.availabilityOffer?.linkedOffer?.location.timeZone,
                    )}`
                }
                return this.getOfferEndTime
            },
            getOfferLocation() {
                return `${this.reservation.availabilityOffer?.linkedOffer?.location.latitude} ${this.reservation.availabilityOffer?.linkedOffer?.location.longitude}`
            },
            handIcon() {
                return this.isSmartphoneLarge ? 'hand-down' : 'hand'
            },

            declineModalProperties(status) {
                return status === this.reservationStatus.DECLINED
                    ? {
                          label: this.$t('components.reservations.reservation_status.actions.DECLINE'),
                          action: this.reservationStatus.DECLINED,
                          heading: this.$t('components.reservations.reservation_status.modal.decline_or_make_unavailable'),
                      }
                    : {
                          label: this.$t('components.reservations.reservation_status.actions.CANCEL'),
                          action: this.reservationStatus.CANCELED,
                          heading: this.$t('components.reservations.reservation_status.modal.cancel_or_make_unavailable'),
                      }
            },

            getDeclineModalProperties() {
                return {
                    label: this.$t('components.reservations.reservation_status.actions.DECLINE'),
                    action: this.reservationStatus.DECLINED,
                    heading: this.$t('components.reservations.reservation_status.modal.decline_or_make_unavailable'),
                }
            },

            getCancelModalProperties() {
                return {
                    label: this.$t('components.reservations.reservation_status.actions.CANCEL'),
                    action: this.reservationStatus.CANCELED,
                    heading: this.$t('components.reservations.reservation_status.modal.cancel_or_make_unavailable'),
                }
            },
            showAddToCalendarButton() {
                return !this.isReservationInPast && this.reservation.status === this.reservationStatus.ACCEPTED
            },
            getReservationLocation() {
                return `${this.reservation?.availabilityOffer.linkedOffer.location.address}, ${this.reservation?.availabilityOffer.linkedOffer.location.city.name}`
            },
            getDescriptionForCalendar() {
                const user = this.getPerformer
                return `${this.$t('components.reservations.reservation_page.description.booked_by')}: ${
                    this.reservation.initiator.firstName
                } ${this.reservation.initiator.lastName} ${this.$t(
                    'components.reservations.reservation_page.description.booked_to',
                )} ${user?.firstName} ${user?.lastName}. ${
                    this.reservation.description
                        ? `<br>${this.$t('common.microcopy.description')}: ${this.reservation.description}`
                        : ''
                } <br>${this.$t('components.reservations.reservation_page.link_to_reservation')}: ${window.location.href}`
            },
            getPerformer() {
                return this.reservation.availabilityOffer?.availability?.availabilityUsers?.find(
                    (item) => item.function === UserAvailabilityEnum.PERFORMER,
                ).user
            },
            isReservationInPast() {
                return new Date() > this.eventStartTimestamp
            },
            isReservationEndTimePast() {
                return new Date() > this.eventEndTimestamp - 5 * 60 * 1000
            },
            eventStartTimestamp() {
                const eventDateTime = `${this.getOfferDate} ${this.getOfferStartTime}`

                const parts = eventDateTime.split(' ')
                const dateParts = parts[0].split('.')
                const timeParts = parts[1].split(':')

                const date = new Date(dateParts[2], dateParts[1] - 1, dateParts[0], timeParts[0], timeParts[1])

                return date.getTime()
            },
            eventEndTimestamp() {
                const eventDateTime = `${this.getOfferDate} ${this.getOfferEndTime}`

                const parts = eventDateTime.split(' ')
                const dateParts = parts[0].split('.')
                const timeParts = parts[1].split(':')

                const date = new Date(dateParts[2], dateParts[1] - 1, dateParts[0], timeParts[0], timeParts[1])

                return date.getTime()
            },

            isEventInMoreThanOneMinute() {
                const minutesDifference = Math.floor((this.eventStartTimestamp - new Date()) / 60000)
                return minutesDifference >= 1 ? true : false
            },

            isEventInMoreThan20Minutes() {
                const minutesDifference = Math.floor((this.eventStartTimestamp - new Date()) / 60000)
                return minutesDifference >= 20 ? true : false
            },

            isEventEligibleToReschedule() {
                const minutesDifference = Math.floor((this.eventStartTimestamp - new Date()) / 60000)
                return minutesDifference >= this.reservation?.availabilityOffer?.linkedOffer.rescheduleLimitForInitiators &&
                    !this.isPublicReservationBlocked
                    ? true
                    : false
            },

            isEventEligibleToCancel() {
                const minutesDifference = Math.floor((this.eventStartTimestamp - new Date()) / 60000)
                return minutesDifference >= this.reservation?.availabilityOffer?.linkedOffer.cancelLimitForInitiators &&
                    !this.isPublicReservationBlocked
                    ? true
                    : false
            },

            isEventLengthGreaterThan5Minutes() {
                return this.reservation.endTime - this.reservation.startTime > 5
            },

            isFinishReservationAvailable() {
                return (
                    this.isReservationInPast && this.isOfferOwner && this.reservation.status === this.reservationStatus.ACCEPTED
                )
            },
            isFinishEearlyReservationAvailable() {
                return (
                    this.isOfferOwner &&
                    !this.isReservationEndTimePast &&
                    (this.reservation.status === this.reservationStatus.ACCEPTED ||
                        this.reservation.status === this.reservationStatus.DONE ||
                        this.reservation.status === this.reservationStatus.NO_SHOW) &&
                    !this.reservation.allowOverlapAfterMinutes &&
                    this.isEventLengthGreaterThan5Minutes
                )
            },
            isCancelFinishEarlyReservationAvailable() {
                return (
                    this.isOfferOwner &&
                    !this.isReservationEndTimePast &&
                    (this.reservation.status === this.reservationStatus.ACCEPTED ||
                        this.reservation.status === this.reservationStatus.DONE ||
                        this.reservation.status === this.reservationStatus.NO_SHOW) &&
                    this.reservation.allowOverlapAfterMinutes
                )
            },

            showSendSms() {
                return (
                    this.isOfferOwner &&
                    !this.isReservationInPast &&
                    this.reservation.status === this.reservationStatus.ACCEPTED &&
                    this.isSmartphoneLarge
                )
            },

            isPublicReservationBlocked() {
                return this.reservation.availabilityOffer?.linkedOffer.type === OfferReservationTypeEnum.BLOCK_PUBLIC_RESERVATIONS
            },
        },

        methods: {
            finishReservationEarly(payload) {
                CalendarEventAPI.finishEarly(this.reservation.id, payload.minutes)
                    .then(
                        () => (
                            (this.reservation.allowOverlapAfterMinutes = payload.minutes),
                            this.$toast.success(this.$t('common.microcopy.success'))
                        ),
                    )
                    .catch((error) => {
                        const calendarEventError = error?.error?.invalidFields[0]
                        if (calendarEventError.field === 'calendarEventId') {
                            switch (calendarEventError.message) {
                                case CalendarEventEnums.ERRORS.NOT_PERMISSIONED:
                                    this.$toast.error(this.$t('common.errors.common_error'))
                                    break
                                case CalendarEventEnums.ERRORS.MINIMUM_5:
                                    this.$toast.error(this.$t(`components.reservations.errors.minimum_5`))
                                    break
                                case CalendarEventEnums.ERRORS.OVERLAP_TOO_LARGE:
                                    this.$toast.error(this.$t(`components.reservations.errors.overlap_too_large`))
                                    break
                                case CalendarEventEnums.ERRORS.UNAVAILABLE_FOR_ALLOWING_OVERLAP:
                                    this.$toast.error(this.$t('components.preservations.errors.unavailable_for_allowing_overlap'))
                                    break
                                default:
                                    break
                            }
                        } else {
                            this.$toast.error(this.$t('common.errors.common_error'))
                        }
                    })
                this.toggleFinishEarlyReservationModal()
            },
            cancelFinishEarlyReservation() {
                CalendarEventAPI.finishEarly(this.reservation.id, 0)
                    .then(
                        () => (
                            (this.reservation.allowOverlapAfterMinutes = null),
                            this.$toast.success(this.$t('common.microcopy.success'))
                        ),
                    )
                    .catch((error) => {
                        const calendarEventError = error?.error?.invalidFields[0]
                        if (calendarEventError.field === 'calendarEventId') {
                            switch (calendarEventError.message) {
                                case CalendarEventEnums.ERRORS.NOT_PERMISSIONED:
                                    this.$toast.error(this.$t('common.errors.common_error'))
                                    break
                                case CalendarEventEnums.ERRORS.MINIMUM_5:
                                    this.$toast.error(this.$t(`components.reservations.errors.minimum_5`))
                                    break
                                case CalendarEventEnums.ERRORS.OVERLAP_TOO_LARGE:
                                    this.$toast.error(this.$t(`components.reservations.errors.overlap_too_large`))
                                    break
                                case CalendarEventEnums.ERRORS.UNAVAILABLE_FOR_ALLOWING_OVERLAP:
                                    this.$toast.error(this.$t('components.preservations.errors.unavailable_for_allowing_overlap'))
                                    break
                                default:
                                    break
                            }
                        } else {
                            this.$toast.error(this.$t('common.errors.common_error'))
                        }
                    })
                this.toggleCancelFinishEarlyReservationModal()
            },
            calendarButtonClick() {
                navigator.clipboard.writeText(this.reservation?.initiator?.email)
            },

            getCalendarEvent() {
                this.$startLoader()
                return CalendarEventAPI.getById(this.$route.params.id)
                    .then((res) => {
                        this.reservation = res
                    })
                    .catch((error) => {
                        if (
                            error.error.message === CalendarEventEnums.ERRORS.NOT_PERMISSIONED ||
                            error.error.message === CalendarEventEnums.ERRORS.NOT_FOUND
                        ) {
                            this.$toast.error(this.$t('common.errors.common_error'))
                            this.$router.push({ name: RoutesEnum.INDEX.HOME })
                        }
                    })
                    .finally(() => this.$stopLoader())
            },
            changeReservationStatus(status, setUnavailable = null) {
                this.isLoading = true
                CalendarEventAPI.changeReservationStatus({
                    calendarEventId: Number(this.$route.params.id),
                    status,
                })
                    .then(() => {
                        this.getCalendarEvent().then(() => (this.isLoading = false))
                        this.isDeclineReservationModalOpen = false
                        this.isFinishReservationModalOpen = false
                    })
                    .then(() => {
                        if (setUnavailable) {
                            CalendarEventAPI.makeSlotUnavailable(
                                this.reservation.date,
                                this.reservation.availabilityOffer.id,
                                this.reservation.startTime,
                            )
                                .then(() => (this.isLoading = false))
                                .catch(() => {
                                    this.$toast.error(this.$t('common.errors.common_error'))
                                })
                        }
                    })
                    .catch((error) => {
                        const calendarEventError = error?.error?.invalidFields[0]
                        if (calendarEventError.field === 'calendarEventId') {
                            switch (calendarEventError.message) {
                                case CalendarEventEnums.ERRORS.TIME_MUST_BE_IN_THE_FUTURE:
                                    this.$toast.error(this.$t('components.reservations.errors.cannot_edit_past_reservations'))
                                    break
                                case CalendarEventEnums.ERRORS.MINIMUM_DIFFERENCE_IS_60min:
                                    this.$toast.error(this.$t(`components.reservations.errors.mins_ahead_60`))
                                    break
                                case CalendarEventEnums.ERRORS.MINIMUM_DIFFERENCE_IS_20min:
                                    this.$toast.error(this.$t(`components.reservations.errors.mins_ahead_20`))
                                    break
                                case CalendarEventEnums.ERRORS.TIME_MUST_BE_IN_THE_PAST:
                                    this.$toast.error(this.$t('components.reservations.errors.time_must_be_in_the_past'))
                                    break
                                default:
                                    break
                            }
                        } else {
                            this.$toast.error(this.$t('common.errors.common_error'))
                        }

                        this.isLoading = false
                        this.isDeclineReservationModalOpen = false
                        this.isFinishReservationModalOpen = false
                    })
            },
            rescheduleReservation() {
                this.$router.push({
                    name: RoutesEnum.RESERVATIONS.RESCHEDULE,
                    params: {
                        offerId: this.reservation.availabilityOffer.linkedOffer.id,
                        calendarEventId: this.reservation.id,
                        availabilityOfferId: this.reservation.availabilityOffer.id,
                    },
                    query: {
                        categoryIds: this.reservation.calendarEventItems.map(
                            (calendarEventItem) => calendarEventItem.offerCategory.id,
                        ),
                        selectedUserId: this.reservation.availabilityOffer.id,
                    },
                })
            },
            getGoogleMapLocation(coordinates) {
                return `https://www.google.com/maps?daddr=${coordinates?.latitude},${coordinates?.longitude}`
            },
            getDate(date) {
                return dayjs(date).format('DD.MM.YYYY')
            },
            getTime(time, timeZone) {
                return availabilityTransformer.minutesDateToLocalTZTime(time, this.reservation.date, timeZone)
            },

            toogleSatusCancelModal() {
                if (
                    this.isOfferOwner &&
                    this.reservation.status === this.reservationStatus.ACCEPTED &&
                    this.isEventInMoreThanOneMinute
                ) {
                    this.setDeclineReservationModalProprties(this.reservationStatus.CANCELED)
                    this.isDeclineReservationModalOpen = !this.isDeclineReservationModalOpen
                } else {
                    this.isStatusCancelModalOpen = !this.isStatusCancelModalOpen
                }
            },
            closeSatusCancelModal() {
                this.changeReservationStatus(this.reservationStatus.CANCELED)
                this.toogleSatusCancelModal()
            },
            toggleDeclineReservationModal() {
                this.setDeclineReservationModalProprties(this.reservationStatus.DECLINED)
                this.isDeclineReservationModalOpen = !this.isDeclineReservationModalOpen
            },

            setDeclineReservationModalProprties(status) {
                this.declineReservationModalProperties =
                    status === this.reservationStatus.DECLINED ? this.getDeclineModalProperties : this.getCancelModalProperties
            },
            toggleFinishReservationModal() {
                this.isFinishReservationModalOpen = !this.isFinishReservationModalOpen
            },
            toggleFinishEarlyReservationModal() {
                this.isFinishEarlyReservationModalOpen = !this.isFinishEarlyReservationModalOpen
            },
            toggleCancelFinishEarlyReservationModal() {
                this.isCancelFinishEarlyReservationModalOpen = !this.isCancelFinishEarlyReservationModalOpen
            },
            sendMessage() {
                const link = document.createElement('a')
                const text = `Pozdrav! Podsećamo Vas na Vašu rezervaciju za ${this.reservation?.availabilityOffer?.linkedOffer?.titleTextContent.originalText} zakazanu za ${this.getOfferDate} u ${this.getOfferStartTime}. Molimo Vas da dođete na vreme. Hvala što birate bookeraj!`
                link.href = `sms://${this.reservation?.initiator?.mobile};?&body=${text.replace(' ', '%20')}`
                link.textContent = 'Send SMS'
                document.body.appendChild(link)
                link.click()
            },

            openClientHistoryModal() {
                this.isClientHistoryModalOpen = true
            },

            closeClientHistoryModal() {
                this.isClientHistoryModalOpen = false
            },

            openClientRecordModal() {
                this.isClientRecordModalOpen = true
            },

            closeClientRecordModal() {
                this.isClientRecordModalOpen = false
            },

            openReservationNoteModal() {
                this.isReservationNoteModalOpen = true
            },

            closeReservationNoteModal() {
                this.isReservationNoteModalOpen = false
            },

            onModeratorNoteSaved(payload) {
                this.reservation.moderatorNote = payload
                this.isReservationNoteModalOpen = false
            },

            openEditPriceModal() {
                this.isEditPriceModalOpen = true
            },

            closeEditPriceModal() {
                this.isEditPriceModalOpen = false
            },

            onPriceUpdated() {
                this.getCalendarEvent()
                this.closeEditPriceModal()
            },
        },

        mounted() {
            this.getCalendarEvent()
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .single-reservation {
        padding-bottom: 20px;

        &__note {
            color: $color--accent-1;
        }

        &__cover {
            min-width: 100%;
            text-align: center;
            &-image {
                border-radius: 30px;
                min-height: 250px;
                object-fit: cover;
            }
        }

        &__actions {
            display: flex;
            &--item {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: center;
                margin: 0 10px;
                cursor: pointer;

                &--history {
                    color: $color-blue--facebook;
                }

                &--records {
                    color: $color--accent-9;
                }

                &--note {
                    color: $color--accent-1;
                }

                p {
                    text-align: center;
                    width: 100%;
                }

                span .icon {
                    cursor: pointer;
                }
            }
        }

        &__categories {
            color: $color--accent-1-lite;
            font-weight: 500;
            p {
                display: list-item;
                margin-left: 10px;
            }
        }

        &__header {
            display: flex;
            align-items: center;

            .info-tip {
                margin-right: 10px;
            }
        }

        &__info {
            > * {
                min-width: 150px;
            }
            width: 100%;
            display: flex;
            justify-content: space-around;
            align-items: center;
            @include breakpoint(tablet) {
                > * {
                    min-width: 120px;
                    margin: 10px 0 !important;
                }
            }
            @include breakpoint(tablet-small) {
                flex-direction: column;
                > * {
                    margin: 10px 0px;
                }
            }
            &--icon {
                margin-top: -15px;
            }
            &--user {
                display: flex;
                justify-content: center;
                align-items: center;
                flex-direction: column;
                min-height: 110px;
                text-align: center;

                &--icon {
                    &-first {
                        margin-top: 10.5px;
                        width: 100%;
                    }
                    &-second {
                        margin-top: 5.5px;
                    }
                }
                &--paragraph {
                    &-first {
                        margin-top: 10.5px;
                    }
                    &-second {
                        margin-top: 5.5px;
                        text-align: center;
                    }
                }
            }
        }
        &__location {
            display: flex;
            justify-content: center;
            align-items: center;
            font-weight: 500;
            font-size: $font-size--heading-small;
            color: $color-link;
            a {
                text-align: center;
            }
            .icon {
                margin: 0 10px;
            }
        }
        &__phone {
            display: flex;
            justify-content: center;
            align-items: center;
            font-weight: 500;
            font-size: $font-size--header-copy;
            @include breakpoint(smartphone-large) {
                flex-direction: column;
            }

            .icon {
                margin: 0 10px;
            }

            &--name {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                margin-right: 5px;
            }

            &--text {
                color: $color-link;
            }
        }
        &__time {
            display: flex;
            justify-content: center;
            align-items: center;
            font-weight: 500;
            font-size: $font-size--header-copy;
            .icon {
                margin: 0 10px;
            }
            @include breakpoint(smartphone-large) {
                display: flex;
                flex-wrap: wrap;
            }
        }

        &__price {
            display: flex;
            justify-content: center;
            align-items: center;
            color: $color--accent-10;
            margin-top: 20px;

            &--paid {
                text-decoration: line-through;
                margin-right: 10px;
                color: $color--txt-base;
            }

            &--hidden {
                @include hiddenBlur();
            }

            .icon {
                margin-left: 10px;
                cursor: pointer;
                color: $color--accent-1;
            }
        }

        &__description {
            word-break: break-all;
            text-align: center;
        }
    }

    .add-to-calendar {
        max-width: 300px;
        position: relative;
        &__button {
            display: flex;
            justify-content: center;
            align-items: center;
            box-shadow: 0 0;
        }
        &__info {
            position: absolute;
            bottom: 45px;
            left: 190px;
            &--ru {
                left: 245px;
            }
        }
    }

    .subdomain-layout {
        &--navy_black,
        &--black {
            .single-reservation {
                border-radius: 30px;
                background-color: $subdomain-deep;

                &__info {
                    &--icon {
                        color: $subdomain-very-light;
                    }

                    &--user {
                        color: $color-white;
                    }
                }
            }

            .add-to-calendar__button {
                .atcb-initialized {
                    button {
                        background-color: $subdomain-black2;
                        border-color: $subdomain-black2;
                        .atcb-text {
                            color: $color-white;
                        }

                        .atcb-icon-trigger {
                            color: $color-white;
                        }
                    }
                }
            }
        }
    }
</style>
